import { mapUser } from '@/modules/Users/mappers';
import {
  ActivityCategories,
  ActivityCategoryActivityTypes,
  ActivityType,
  ActivityTypeIssueTypes,
  BillableIssueTypes,
  FilterType,
  StatusFilter,
  StatusValue,
  TypologyValue,
} from '@/shared';
import { actionRequiredStatus, DateUtility } from '@/utils';
import {
  Request,
  RequestActivity,
  RequestActivityCategory,
  RequestDetails,
  RequestKpi,
  RequestTotals,
} from '../domain';
import {
  RequestActivityJson,
  RequestDetailsJson,
  RequestJson,
  RequestKpiJson,
  RequestsFilters,
  RequestsFiltersReturn,
  RequestTotalsJson,
} from '../types';

export const mapJiraReference = (reference: string) =>
  reference.slice(reference.lastIndexOf('-') + 1);

export const mapRequestTotals = (totals: RequestTotalsJson): RequestTotals => ({
  requestsOpen: totals?.requestsOpen,
  requestsFinalized: totals?.requestsFinalized,
  requestsCanceled: totals?.requestsCanceled,
  requestsArchived: totals?.requestsArchived,
  usedSeconds: totals?.usedSeconds,
  unusedSeconds: totals?.unusedSeconds,
  nrRequestsOfDiscovery: totals?.nrRequestsOfDiscovery,
  nrRequestsOfDevelopment: totals?.nrRequestsOfDevelopment,
  nrRequestsOfDilligence: totals?.nrRequestsOfDilligence,
  extraHoursPackages: totals?.extraHoursPackages ?? [],
  nrRequestsSubmitted: totals?.nrRequestsSubmitted,
  nrRequestsInEvaluation: totals?.nrRequestsInEvaluation,
  nrRequestsInExecution: totals?.nrRequestsInExecution,
  nrRequestsConcluded: totals?.nrRequestsConcluded,
});

const mapRequest = (request: RequestJson): Request => ({
  id: request.id,
  typology: request.typology,
  priority: request.priority,
  status: request.status,
  reference: mapJiraReference(request.reference),
  title: request.title,
  createdAt: new Date(request.createdAt),
  createdBy: request.createdBy && mapUser(request.createdBy),
  userResponsable: request.userResponsable && mapUser(request.userResponsable),
  analysisSeconds: request.analysisSeconds ?? 0,
  estimatedSeconds: request.estimatedSeconds,
  usedSeconds: request.usedSeconds,
});

const mapRequestKpi = (request: RequestKpiJson): RequestKpi => ({
  id: request.id,
  typology: request.typology,
  status: request.status,
  reference: mapJiraReference(request.reference),
  title: request.title,
  createdAt: new Date(request.createdAt),
  createdBy: request.createdBy && mapUser(request.createdBy),
  estimatedSeconds: request.estimatedSeconds,
  usedSeconds: request.usedSeconds,
});

export const mapRequestDetails = (
  request: RequestDetailsJson,
): RequestDetails => ({
  id: request.id,
  typology: request.typology,
  priority: request.priority,
  status: request.status,
  reference: request.reference,
  title: request.title,
  description: request.description,
  createdAt: new Date(request.createdAt),
  createdBy: request.createdBy && mapUser(request.createdBy),
  userResponsible: request.userResponsible && mapUser(request.userResponsible),
});

export const mapRequests = (requests: RequestJson[]): Request[] =>
  requests.map(mapRequest);

export const mapRequestsKpis = (requests: RequestKpiJson[]): RequestKpi[] =>
  requests.map(mapRequestKpi);

export const mapRequestFilters = ({
  onlyActiveRequests = false,
  activeTypologies = [],
  filter,
  filterBy,
  dateRange,
  status,
}: RequestsFilters): RequestsFiltersReturn => {
  const filters: string[] = [];

  if (activeTypologies?.length) {
    filters.push(`typologies__in__[${activeTypologies.join(',')}]`);
  } else {
    const typologyIds = Object.values(TypologyValue)
      .filter((enumValue) => typeof enumValue == 'number')
      .join(',');
    filters.push(`typologies__not in__[${typologyIds}]`);
  }

  if (filter && filterBy == FilterType.Claimant) {
    filters.push(`createdBy__=__${filter}`);
  }

  if (filter && filterBy == FilterType.Status) {
    filters.push(`status__in__[${mapStatusFilterToStatus(+filter)}]`);
  }

  if (filter && filterBy == FilterType.RequireAction) {
    const allStatus = Object.values(StatusValue).filter(
      (v) => typeof v === 'number',
    );

    const filterStatus = allStatus.filter((status) => {
      return filter.toLowerCase() == 'true'
        ? actionRequiredStatus.includes(status as StatusValue)
        : !actionRequiredStatus.includes(status as StatusValue);
    });
    filters.push(
      `status__in__[${filterStatus.map((value) => value).join(',')}]`,
    );
  }

  const { fromDate, toDate } =
    DateUtility.extractDateStringQueryParamsFromRange(dateRange);

  if (fromDate) {
    filters.push(`fromDate__>=__${fromDate}`);
  }

  if (toDate) {
    filters.push(`toDate__<=__${toDate}`);
  }

  return {
    onlyActiveRequests,
    filter: filters.length ? filters.join(';') : undefined,
    status,
  };
};

export const mapStatusFilterToStatus = (statusFilter: StatusFilter) => {
  switch (statusFilter) {
    case StatusFilter.Waiting:
      return [
        StatusValue.Submitted,
        StatusValue.Accepted,
        StatusValue.ToDo,
        StatusValue.OnHold,
      ];
    case StatusFilter.Analysis:
      return [StatusValue.Analysis];
    case StatusFilter.Blocked:
      return [StatusValue.Blocked];
    case StatusFilter.Quoted:
      return [StatusValue.Quoted];
    case StatusFilter.Canceled:
      return [StatusValue.Canceled];
    case StatusFilter.Done:
      return [StatusValue.Done];
    case StatusFilter.InProgress:
      return [StatusValue.InProgress];
    case StatusFilter.InQA:
      return [StatusValue.InQA];
    case StatusFilter.Pending:
      return [StatusValue.Pending];
    case StatusFilter.Rejected:
      return StatusValue.Rejected;
    default:
      return [];
  }
};

const mapActivityByType = (
  activityType: ActivityType,
  activities: RequestActivityJson[],
) => {
  const activityIssueTypes = ActivityTypeIssueTypes[activityType];
  const selectedActivities = activities.filter((activity) =>
    activityIssueTypes.includes(activity.typeId),
  );

  const { billableSeconds, nonBillableSeconds, estimatedSeconds } =
    selectedActivities.reduce(
      (acc, activity) => {
        const isBillable = BillableIssueTypes.includes(activity.typeId);
        acc.billableSeconds = isBillable
          ? acc.billableSeconds + activity.timeSpentSeconds
          : acc.billableSeconds;
        acc.estimatedSeconds = activity.estimatedSeconds;
        acc.nonBillableSeconds = !isBillable
          ? acc.nonBillableSeconds + activity.timeSpentSeconds
          : acc.nonBillableSeconds;
        return acc;
      },
      {
        billableSeconds: 0,
        estimatedSeconds: 0,
        nonBillableSeconds: 0,
      },
    );
  return {
    type: activityType,

    billableSeconds,
    nonBillableSeconds,
    estimatedSeconds,
    totalSeconds: billableSeconds + nonBillableSeconds,
  };
};

export const mapRequestActivities = (
  activities: RequestActivityJson[],
): RequestActivityCategory[] => {
  return ActivityCategories.map((category) => {
    const mappedActivities = ActivityCategoryActivityTypes[category]
      .map<RequestActivity>((activityType) =>
        mapActivityByType(activityType, activities),
      )
      .filter(
        (activity) =>
          activity.totalSeconds > 0 || activity.estimatedSeconds > 0,
      );

    const { billableSeconds, estimatedSeconds, nonBillableSeconds } =
      mappedActivities.reduce(
        (acc, activity) => {
          return {
            billableSeconds: acc.billableSeconds + activity.billableSeconds,
            estimatedSeconds: acc.estimatedSeconds + activity.estimatedSeconds,
            nonBillableSeconds:
              acc.nonBillableSeconds + activity.nonBillableSeconds,
          };
        },
        {
          billableSeconds: 0,
          estimatedSeconds: 0,
          nonBillableSeconds: 0,
        },
      );

    return {
      category,
      activities: mappedActivities,
      billableSeconds,
      estimatedSeconds,
      nonBillableSeconds,
      totalSeconds: billableSeconds + nonBillableSeconds,
    };
  }).filter(
    (category) => category.totalSeconds > 0 || category.estimatedSeconds > 0,
  );
};
